import React, {
	createContext,
	useContext,
	useImperativeHandle,
	useMemo,
} from 'react';
import {observer} from 'mobx-react-lite';
import {useUpload, UseUploadParams, UseUploadType} from '../hooks';
import {UppyStateStore} from '../store';
import {UppyFileMeta} from '../types';

export type UploadContextType<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = UseUploadType<TMeta, TStore>;

export const UploadContext = createContext<UploadContextType | undefined>(
	undefined,
);

export const useUploadProvider = <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>() => {
	const context = useContext(
		UploadContext as React.Context<
			UploadContextType<TMeta, TStore> | undefined
		>,
	);
	if (context === undefined) {
		throw new Error(
			'`useUploadProvider` must be used within a `UploadProvider`',
		);
	}
	return context;
};

export type UploadProviderRef<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> =
	| React.RefObject<UploadContextType<TMeta, TStore>>
	| ((node: UploadContextType<TMeta, TStore>) => void)
	| null;

export type UploadProviderChildrenProps<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = UploadContextType<TMeta, TStore>;

export type UploadProviderProps<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = UseUploadParams<TMeta, TStore> & {
	ref?: UploadProviderRef;
	children:
		| React.ReactNode
		| ((props: UploadProviderChildrenProps<TMeta, TStore>) => React.ReactNode);
};

const UploadProviderInner = <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>(
	{children, ...props}: UploadProviderProps<TMeta, TStore>,
	ref: UploadProviderRef<TMeta, TStore>,
) => {
	const upload = useUpload<TMeta, TStore>(props);

	const contextValues = useMemo(() => upload, [upload]);

	useImperativeHandle(ref, () => contextValues, [contextValues]);

	return (
		<UploadContext.Provider value={contextValues as any}>
			{typeof children === 'function' ? children(contextValues) : children}
		</UploadContext.Provider>
	);
};

export const UploadProviderPrimitive = React.forwardRef<
	UploadContextType,
	UploadProviderProps
>(UploadProviderInner) as <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>(
	props: UploadProviderProps<TMeta, TStore>,
) => JSX.Element;

/**
 * Upload context provider
 * @example
 * <UploadProvider ref={uploadRef}>
 * 	...
 * </UploadProvider>
 * @example
 * <UploadProvider ref={uploadRef}>
 * 	{(upload) => (
 * 		...
 * 	)}
 * </UploadProvider>
 */
export const UploadProvider = observer(UploadProviderPrimitive) as <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>(
	props: UploadProviderProps<TMeta, TStore>,
) => JSX.Element;

export default UploadProvider;
